/*
 * Purpose: API base URL — same-origin /api on app host; cross-origin only on Pages
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { COMPANY_INFO } from '@/lib/companyInfo';
import { SAME_ORIGIN_API_HOSTS } from '@/lib/domainConfig';

function readEnvApiBase(): string {
  const api = import.meta.env.VITE_API_URL as string | undefined;
  const inbound = import.meta.env.VITE_INBOUND_API_URL as string | undefined;
  const auth = import.meta.env.VITE_AUTH_API_BASE as string | undefined;
  return (api || inbound || auth || '').replace(/\/$/, '');
}

function prefersSameOriginApi(): boolean {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  if (SAME_ORIGIN_API_HOSTS.has(hostname)) return true;
  // Render PR previews: autoblocks-run-pr-123.onrender.com
  if (hostname.startsWith('autoblocks-run') && hostname.endsWith('.onrender.com')) return true;
  return false;
}

function mustUseRemoteApiHost(): boolean {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  // autoblocks.run / www.autoblocks.run are currently served without /api rewrite.
  if (hostname === 'autoblocks.run' || hostname === 'www.autoblocks.run') return true;
  if (hostname.endsWith('.pages.dev')) return true;
  return false;
}

/** Absolute API origin, or '' for same-origin (Vite proxy or Render /api rewrite). */
export function getApiBase(): string {
  const env = readEnvApiBase();
  // Cloudflare Pages on autoblocks.run has no /api rewrite — honor explicit build-time API base.
  if (env) return env;
  if (mustUseRemoteApiHost()) return COMPANY_INFO.officialApiUrl;
  if (prefersSameOriginApi()) return '';
  if (import.meta.env.PROD && typeof window === 'undefined') {
    return COMPANY_INFO.renderApiUrl;
  }
  if (typeof window !== 'undefined') return '';
  return 'http://localhost:3001';
}

/** Prefix a path with the API origin when cross-host. */
export function apiUrl(path: string): string {
  const base = getApiBase();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (!base) return normalized;
  return `${base}${normalized}`;
}

export function isRemoteApi(): boolean {
  return Boolean(getApiBase());
}

/** @deprecated Use isRemoteApi */
export function isRemoteInboundApi(): boolean {
  return isRemoteApi();
}

export function gmailWebhookPostUrl(inboxId: string): string {
  return apiUrl(`/api/webhooks/gmail/${inboxId}`);
}

export function gmailWebhookLatestUrl(inboxId: string): string {
  return apiUrl(`/api/webhooks/gmail/${inboxId}/latest`);
}

/** User-facing hint when auth fetch fails (network / API down). */
export function authFetchErrorMessage(err: unknown): string {
  if (err instanceof TypeError && /fetch/i.test(err.message)) {
    if (prefersSameOriginApi()) {
      return 'Could not reach the sign-in API. Check that autoblocks-run-api is running on Render.';
    }
    return 'Could not reach the API — check api.autoblocks.run DNS or use the app at preview.autoblocks.run.';
  }
  return err instanceof Error ? err.message : 'Request failed';
}
